import {
	Children,
	cloneElement,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useZoomFactor } from '~/hooks/useZoomFactor';
import {
	setIsOpeningCase,
	setMaxTabIndex,
	setReportVisible,
	setShopKitOpen,
	setShopOpenCaseVisible,
	setShopPlayerItemsOpen,
	setTabIndex,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { TabLine } from '../TabLine';
import { StyledContainer, StyledGlow } from './TabsContainer.styled';

const TabsContainer = ({
	children,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => {
	const dispatch = useLayoutDispatch();
	const tabIndex = useLayoutSelector((state) => state.mainMenu.tabIndex);
	const maxTabIndex = useLayoutSelector((state) => state.mainMenu.maxTabIndex);
	const reportVisible = useLayoutSelector(
		(state) => state.mainMenu.reportVisible,
	);
	const shopKitOpen = useLayoutSelector((state) => state.shop.shopKitOpen);
	const shopPlayerItemsOpen = useLayoutSelector(
		(state) => state.shop.shopPlayerItemsOpen,
	);
	const shopOpenCaseVisible = useLayoutSelector(
		(state) => state.shop.shopOpenCaseVisible,
	);

	const resetShopState = () => {
		if (shopKitOpen) {
			dispatch(setShopKitOpen(false));
		}
		if (shopPlayerItemsOpen) {
			dispatch(setShopPlayerItemsOpen(false));
		}
		if (shopOpenCaseVisible) {
			dispatch(setShopOpenCaseVisible(false));
			dispatch(setIsOpeningCase(false));
		}
	};

	const handleClick = (index: number) => {
		dispatch(setTabIndex(index));
		dispatch(setReportVisible(false));
		resetShopState();
	};

	const zoomFactor = useZoomFactor();
	const containerRef = useRef<HTMLDivElement | null>(null);
	const [scrollable, setScrollable] = useState(false);
	const [glowPosition, setGlowPosition] = useState({ left: 0, top: 0 });

	const updateGlowPosition = useCallback(() => {
		const tabElement = document.getElementById(`tab#${tabIndex}`);
		if (tabElement !== null) {
			const rect = tabElement.getBoundingClientRect();
			setGlowPosition({
				left: rect.left / zoomFactor + rect.width / zoomFactor / 2,
				top: rect.top / zoomFactor,
			});
		}
	}, [tabIndex, zoomFactor]);

	useEffect(() => {
		const tabCount = Children.count(children);
		if (tabCount - 1 !== maxTabIndex) {
			dispatch(setMaxTabIndex(tabCount - 1));
		}
	}, [children, maxTabIndex, dispatch]);

	useEffect(() => {
		const tabElement = document.getElementById(`tab#${tabIndex}`);
		if (tabElement !== null) {
			tabElement.scrollIntoView({
				behavior: 'smooth',
				block: 'nearest',
				inline: 'center',
			});
		}
	}, [tabIndex]);

	useEffect(() => {
		const tabElement = document.getElementById(`tab#${tabIndex}`);
		const lineElement = document.getElementById(`tab#line`);
		if (tabElement !== null && lineElement !== null) {
			const tabWidth = window.getComputedStyle(tabElement).width;
			lineElement.style.setProperty('--width', tabWidth);
			lineElement.style.setProperty('--left', `${tabElement.offsetLeft}px`);
		}
		updateGlowPosition();
	}, [tabIndex, updateGlowPosition]);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;

		const update = () => {
			const effectiveWidth = el.clientWidth / (zoomFactor || 1);
			const needsScroll =
				el.scrollWidth > el.clientWidth + 1 || effectiveWidth < 872;
			setScrollable(needsScroll);
			updateGlowPosition();
		};

		update();
		const ro = new ResizeObserver(update);
		ro.observe(el);
		window.addEventListener('resize', update);
		el.addEventListener('scroll', updateGlowPosition);
		return () => {
			ro.disconnect();
			window.removeEventListener('resize', update);
			el.removeEventListener('scroll', updateGlowPosition);
		};
	}, [zoomFactor, updateGlowPosition]);

	return (
		<>
			<StyledContainer ref={containerRef} $scrollable={scrollable} {...props}>
				{Children.map(children, (child: any, index) => {
					return cloneElement(child, {
						id: `tab#${index}`,
						onClick: () => handleClick(index),
						$selected: index === tabIndex,
					});
				})}
				<TabLine id="tab#line" style={{ opacity: reportVisible ? 0 : 1 }} />
			</StyledContainer>
			{createPortal(
				<StyledGlow
					style={{
						left: `${glowPosition.left}px`,
						top: `${glowPosition.top}px`,
						opacity: reportVisible ? 0 : 1,
						transform: `translate(-50%, -70px)`,
					}}
				/>,
				document.body,
			)}
		</>
	);
};

export { TabsContainer };
